import React, { useMemo, useEffect, useState, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import TableContainer from "../../../components/Common/DataTableContainer"
import {
  downloadBalanceReport as downloadBalanceReportAction,
  downloadBookingReport as downloadBookingReportAction,
  downloadInvoices,
  getBookingList,
  resendInvoiceAction,
} from "../../../redux/actions/pvs"
import { Button, Label } from "reactstrap"
import RefreshButton from "components/RefreshButton/RefreshButton"
import { toast } from "react-toastify"
import {
  DOWNLOAD_BALANCE_REPORT_RESET,
  DOWNLOAD_BOOKING_REPORT_RESET,
  DOWNLOAD_INVOICES_RESET,
  RESEND_INVOICE_RESET,
} from "../../../redux/types/pvs"
import ActionDropdown from "components/ActionDropdown"

const useQuery = () => new URLSearchParams(useLocation().search)

const FILTER_OPTIONS = [
  { id: "first_time", field: "First Time", value: "Only" },
  { id: "two_sided", field: "Two Sided", value: "Only" },
  { id: "stall_individual", field: "Individual", value: "Individual" },
  { id: "stall_shared", field: "Shared", value: "Shared" },
  { id: "cash_no_invoice", field: "Cash", value: "No Invoice" },
  { id: "cancelled", field: "Canceled", value: "Canceled" },
]

const ViewBookingList = () => {
  document.title = "PULA Bazaar List"

  const query = useQuery()
  const bazaarId = query.get("id")

  const history = useNavigate()
  const dispatch = useDispatch()

  const token = useSelector(state => state.auth.token)
  const paymentStatistics = useSelector(state => state.pvs.paymentStatistics)
  const bazaarListState = useSelector(state => state.pvs.bazaarList)
  const bookingList = useSelector(state => state.pvs.bookingList)
  const invoicesZipFileLink = useSelector(
    state => state.pvs.invoicesZipFileLink
  )
  const bookingReportFileLink = useSelector(
    state => state.pvs.bookingReportFileLink
  )
  const balanceReportFileLink = useSelector(
    state => state.pvs.balanceReportFileLink
  )
  const resendInvoiceScreen = useSelector(
    state => state.screens.pvs.resendInvoice
  )
  const downloadInvoicesScreen = useSelector(
    state => state.screens.pvs.downloadInvoices
  )
  const downloadBookingReportScreen = useSelector(
    state => state.screens.pvs.downloadBookingReport
  )
  const downloadBalanceReportScreen = useSelector(
    state => state.screens.pvs.downloadBalanceReport
  )

  const [userData, setUserData] = useState([])
  const [activeFilters, setActiveFilters] = useState(new Set())
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const filterPanelRef = useRef(null)

  const toggleFilter = filterId => {
    setActiveFilters(prev => {
      const next = new Set(prev)
      if (filterId === "stall_individual") next.delete("stall_shared")
      if (filterId === "stall_shared") next.delete("stall_individual")
      next.has(filterId) ? next.delete(filterId) : next.add(filterId)
      return next
    })
  }

  // 🔹 Fetch live booking list
  useEffect(() => {
    dispatch(getBookingList({ bazaar_id: bazaarId }, token))
  }, [])

  // 🔹 Update local data once Redux updates
  useEffect(() => {
    setUserData(bookingList)
  }, [bookingList])

  useEffect(() => {
    if (resendInvoiceScreen.success) {
      toast.success(resendInvoiceScreen.message)
      dispatch({ type: RESEND_INVOICE_RESET })
    }
    if (resendInvoiceScreen.error) {
      toast.error(resendInvoiceScreen.error)
      dispatch({ type: RESEND_INVOICE_RESET })
    }
  }, [resendInvoiceScreen])

  useEffect(() => {
    if (downloadInvoicesScreen.success) {
      toast.success(downloadInvoicesScreen.message)

      if (invoicesZipFileLink) {
        const link = document.createElement("a")
        link.href = invoicesZipFileLink
        link.download = "invoices.zip"
        link.click()
      }
      dispatch({ type: DOWNLOAD_INVOICES_RESET })
    }
    if (downloadInvoicesScreen.error) {
      toast.error(downloadInvoicesScreen.error)
      dispatch({ type: DOWNLOAD_INVOICES_RESET })
    }
  }, [downloadInvoicesScreen])

  useEffect(() => {
    if (downloadBookingReportScreen.success) {
      if (bookingReportFileLink) {
        const link = document.createElement("a")
        link.href = bookingReportFileLink
        link.download = "booking-report.zip"
        link.click()
      }
      dispatch({ type: DOWNLOAD_BOOKING_REPORT_RESET })
    }
    if (downloadBookingReportScreen.error) {
      toast.error(downloadBookingReportScreen.error)
      dispatch({ type: DOWNLOAD_BOOKING_REPORT_RESET })
    }
  }, [downloadBookingReportScreen])

  useEffect(() => {
    if (downloadBalanceReportScreen.success) {
      if (balanceReportFileLink) {
        const link = document.createElement("a")
        link.href = balanceReportFileLink
        link.download = "balance-report.zip"
        link.click()
      }
      dispatch({ type: DOWNLOAD_BALANCE_REPORT_RESET })
    }
    if (downloadBalanceReportScreen.error) {
      toast.error(downloadBalanceReportScreen.error)
      dispatch({ type: DOWNLOAD_BALANCE_REPORT_RESET })
    }
  }, [downloadBalanceReportScreen])

  const resendInvoice = registrationId => {
    dispatch(
      resendInvoiceAction({ enc_bazaar_register_id: registrationId }, token)
    )
  }

  const downloadInvoice = () => {
    dispatch(downloadInvoices({ enc_bazaar_id: bazaarId }, token))
  }

  const handleDownloadBookingReport = () => {

    const bazaar = bazaarListState.find(b => b.enc_bazaar_id === bazaarId)

    const paymentStatistic = paymentStatistics.find(
      p => p.combine_group_id === bazaar.combine_group_id
    )

    dispatch(
      downloadBookingReportAction(
        {
          enc_combine_group_id: paymentStatistic.enc_combine_group_id,
          report_type: "excel", //list , excel
        },
        token
      )
    )
  }

  const handleDownloadBalanceReport = () => {
    const bazaar = bazaarListState.find(b => b.enc_bazaar_id === bazaarId)
    const paymentStatistic = paymentStatistics.find(
      p => p.combine_group_id === bazaar.combine_group_id
    )
    dispatch(
      downloadBalanceReportAction(
        {
          enc_combine_group_id: paymentStatistic.enc_combine_group_id,
          report_type: "excel", //list , excel
        },
        token
      )
    )
  }

  const columns = useMemo(
    () => [
      {
        header: "Seller Name",
        accessorKey: "seller_name",
        enableColumnFilter: true,
        enableSorting: true,
        cell: info => {
          const row = info.row.original
          return (
            <div className="d-flex align-items-center gap-2">
              <span>{info.getValue()}</span>
              {row.is_first_time ? (
                <span className="badge bg-info" style={{ fontSize: "0.7rem" }}>
                  First Time
                </span>
              ) : null}
            </div>
          )
        },
      },
      // {
      //   header: "Bazaar Name",
      //   accessorKey: "bazaar_name",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      // },
      // {
      //   header: "Booking Date",
      //   accessorKey: "created_at",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      // },
      {
        header: "Stall No",
        accessorKey: "stall_details",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Stall Type",
        accessorKey: "stall_category",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Two Sided",
        accessorKey: "two_sided",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span
            >
              {value === 0 ? "No" : "Yes"}
            </span>
          )
        },
      },
      {
        header: "Total",
        accessorKey: "payable_amount",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Paid",
        accessorKey: "paid_amount",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Due",
        accessorKey: "due_amount",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Invoice No",
        accessorKey: "invoice_no",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const row = info.row.original
          const paymentStatus =
            row && row.payment_status
              ? String(row.payment_status).toLowerCase()
              : ""
          return (
            <span>
              {paymentStatus === "cancelled"
                ? row.cancelled_invoice_no
                : info.getValue()}
            </span>
          )
        },
      },
      {
        header: "Payment Status",
        accessorKey: "payment_status",
        enableColumnFilter: false,
        enableSorting: true,
        sortingFn: (rowA, rowB, columnId) => {
          const order = {
            pending: 0,
            paid: 1,
            cancelled: 2,
          }

          const a = String(rowA.getValue(columnId) || "").toLowerCase()
          const b = String(rowB.getValue(columnId) || "").toLowerCase()

          const aRank = order[a] !== undefined ? order[a] : 99
          const bRank = order[b] !== undefined ? order[b] : 99

          return aRank - bRank
        },
        cell: info => {
          const status = info.getValue()
          return (
            <span
              className={`badge ${
                status === "Paid"
                  ? "bg-success"
                  : status === "Pending"
                  ? "bg-warning"
                  : "bg-danger"
              }`}
            >
              {status}
            </span>
          )
        },
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const row = info.row.original

          const paymentStatus =
            row && row.payment_status
              ? String(row.payment_status).toLowerCase()
              : ""

          const isPaid =
            paymentStatus === "paid" || paymentStatus === "cancelled"

          const invoiceLink =
            paymentStatus === "cancelled"
              ? row.cancelled_invoice_no_url
              : row.invoice_link

          return (
            <div>
              {isPaid ? (
                invoiceLink ? (
                  <>
                    <ActionDropdown buttonLabel="Invoice">
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          // const link = document.createElement("a")
                          // link.href = invoiceLink
                          // link.download = "invoice.pdf"
                          // link.click()
                          window.open(invoiceLink, "_blank")
                        }}
                      >
                        Download PDF
                      </button>

                      {paymentStatus !== "cancelled" && (
                        <>
                          <button
                            className="dropdown-item"
                            onClick={() =>
                              resendInvoice(row.enc_bazaar_register_id)
                            }
                          >
                            Resend to user
                          </button>

                          <button
                            className="dropdown-item"
                            onClick={() =>
                              history(`/invoice-action/update`, {
                                state: {
                                  type: "update",
                                  id: row.enc_bazaar_register_id,
                                },
                              })
                            }
                          >
                            Update Data
                          </button>

                          <button
                            className="dropdown-item"
                            onClick={() =>
                              history(`/invoice-action/regenerate`, {
                                state: {
                                  type: "cancel_and_regenerate",
                                  id: row.enc_bazaar_register_id,
                                },
                              })
                            }
                          >
                            Cancel & Regenrate
                          </button>
                        </>
                      )}
                    </ActionDropdown>
                  </>
                ) : (
                  <></> // isPaid true but no invoice → show empty
                )
              ) : (
                <button
                  className="btn btn-sm btn-primary me-2"
                  onClick={() =>
                    history(
                      `/add-bazaar-payment?id=${row.enc_bazaar_register_id}`
                    )
                  }
                  title="Add Payment"
                >
                  Add Payment
                </button>
              )}
            </div>
          )
        },
      },
    ],
    [history]
  )
  useEffect(() => {
    const handleClickOutside = e => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) {
        setFilterPanelOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const filteredData = useMemo(() => {
    let data = userData || []
    if (activeFilters.has("first_time")) data = data.filter(r => r.is_first_time)
    if (activeFilters.has("two_sided")) data = data.filter(r => r.two_sided === 1)
    if (activeFilters.has("stall_individual")) data = data.filter(r => String(r.stall_category || "").toLowerCase() === "individual")
    if (activeFilters.has("stall_shared")) data = data.filter(r => String(r.stall_category || "").toLowerCase() === "shared")
    if (activeFilters.has("cash_no_invoice")) data = data.filter(r => String(r.payment_status || "").toLowerCase() === "paid" && (!r.invoice_no || String(r.invoice_no).trim().toUpperCase() === "NA"))
    if (activeFilters.has("cancelled")) data = data.filter(r => String(r.payment_status || "").toLowerCase() === "cancelled")
    return data
  }, [userData, activeFilters])

  const handleRefresh = () => {
    dispatch(getBookingList({ bazaar_id: bazaarId }, token))
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex align-items-center justify-content-between my-4">
          <div>
            <h2 className="h4 mb-0 nom-title">
              {(userData && userData[0] && userData[0].bazaar_name) || ""}
            </h2>
          </div>
          <Button color="primary" onClick={downloadInvoice}>
            Download Invoices
          </Button>
          {/* <ActionDropdown
            buttonLabel="Download"
            className="btn btn-medium btn-primary me-2"
          >
            <button className="dropdown-item" onClick={downloadInvoice}>
              All Invoices
            </button>

            <button
              className="dropdown-item"
              onClick={handleDownloadBookingReport}
            >
              Booking Report
            </button>

            <button
              className="dropdown-item"
              onClick={handleDownloadBalanceReport}
            >
              Balance Report
            </button>
          </ActionDropdown> */}
          {/* <RefreshButton onRefresh={handleRefresh} /> */}
        </div>

        <TableContainer
          defaultPageSize={20}
          columns={columns}
          data={filteredData}
          initialSorting={[{ id: "payment_status", desc: false }]}
          isGlobalFilter={true}
          isPagination={true}
          isCustomPageSize={true}
          SearchPlaceholder="Search records..."
          pagination="pagination"
          isAddButton={false}
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          theadClass="table-light"
          divClassName="table-responsive mb-4 nominee-tbl-wrapper"
          key={[...activeFilters].sort().join("|")}
          customFilterContent={
            <div className="d-flex align-items-center gap-2 flex-wrap" ref={filterPanelRef}>
              {[...activeFilters].map(filterId => {
                const opt = FILTER_OPTIONS.find(o => o.id === filterId)
                return (
                  <span
                    key={filterId}
                    className="d-inline-flex align-items-center gap-1 px-3 py-1"
                    style={{ background: "#fff", border: "1px solid #dee2e6", borderRadius: 20, fontSize: "0.82rem", fontWeight: 500, whiteSpace: "nowrap" }}
                  >
                    <span style={{ color: "#374151" }}>{opt.field}</span>
                    <button
                      onClick={() => toggleFilter(filterId)}
                      style={{ background: "none", border: "none", padding: "0 0 0 4px", cursor: "pointer", color: "#9ca3af", lineHeight: 1, fontSize: "1rem" }}
                    >
                      ×
                    </button>
                  </span>
                )
              })}

              <div className="position-relative">
              <button
                className="btn btn btn-primary d-flex align-items-center gap-1"
                style={{ borderRadius: 5, padding: "5px 16px", fontSize: "0.86rem" }}
                onClick={() => setFilterPanelOpen(prev => !prev)}
              >
                <i className="bx bx-filter-alt"></i>
                Filters {activeFilters.size > 0 && `(${activeFilters.size})`}
                <i className={`bx bx-chevron-${filterPanelOpen ? "up" : "down"}`} />
              </button>

              {filterPanelOpen && (
                <div
                  className="position-absolute bg-white border rounded shadow-sm"
                  style={{ top: "calc(100% + 6px)", left: 0, zIndex: 1050, minWidth: 230 }}
                >
                  <div className="p-2">
                    {FILTER_OPTIONS.map(opt => (
                      <button
                        key={opt.id}
                        className="dropdown-item rounded d-flex align-items-center gap-2 py-2"
                        style={{ fontSize: "0.875rem" }}
                        onClick={() => { toggleFilter(opt.id); setFilterPanelOpen(false) }}
                      >
                        <span
                          style={{
                            width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                            border: `2px solid ${activeFilters.has(opt.id) ? "#880356" : "#adb5bd"}`,
                            background: activeFilters.has(opt.id) ? "#880356" : "transparent",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                          }}
                        >
                          {activeFilters.has(opt.id) && <i className="bx bx-check" style={{ color: "#fff", fontSize: 11 }} />}
                        </span>
                        <span style={{ color: "#374151" }}>{opt.field}</span>
                      </button>
                    ))}
                  </div>
                  {activeFilters.size > 0 && (
                    <>
                      <div className="border-top" />
                      <div className="p-2">
                        <button
                          className="dropdown-item text-danger rounded py-2"
                          style={{ fontSize: "0.875rem" }}
                          onClick={() => { setActiveFilters(new Set()); setFilterPanelOpen(false) }}
                        >
                          <i className="bx bx-x me-1" />
                          Clear all filters
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              </div>
            </div>
          }
        />
      </div>
    </div>
  )
}

export default ViewBookingList
